import { Clipboard } from "@angular/cdk/clipboard";
import {
  Component,
  inject,
  signal,
  viewChild,
  ViewContainerRef,
} from "@angular/core";
import { DatePipe, Location } from "@angular/common";
import { NzDividerComponent } from "ng-zorro-antd/divider";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzButtonComponent } from "ng-zorro-antd/button";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { NzFormModule } from "ng-zorro-antd/form";
import { ProjectService } from "../../shared/services/project.service";
import {
  catchError,
  firstValueFrom,
  Subject,
  takeUntil,
  throwError,
} from "rxjs";
import {
  NzTreeNodeOptions,
  NzTreeModule,
  NzFormatEmitEvent,
  NzTreeComponent,
  NzTreeNode,
} from "ng-zorro-antd/tree";
import { NzTagComponent } from "ng-zorro-antd/tag";
import { NzIconModule } from "ng-zorro-antd/icon";
import { ActivatedRoute } from "@angular/router";
import { NzSpaceCompactComponent, NzSpaceModule } from "ng-zorro-antd/space";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzMessageService } from "ng-zorro-antd/message";
import { HttpErrorResponse } from "@angular/common/http";
import { httpErrorMessage } from "../../shared/http-error-message";
import { foldS3Keys } from "../../shared/s3-key-tree";
import { FormatBytesPipe } from "../../shared/pipes/format-bytes.pipe";
import { S3Item, TargetStorageClass } from "../../models/s3-item.model";
import {
  StorageClassConfirmComponent,
  StorageClassRequest,
} from "./storage-class-confirm/storage-class-confirm.component";
import { rxResource } from "@angular/core/rxjs-interop";
import { S3BucketInterface } from "../../models/s3-bucket.model";
import { NzSelectComponent, NzOptionComponent } from "ng-zorro-antd/select";
import { CachedProjectService } from "../../shared/services/cached-project.service";
import { NzModalModule, NzModalService } from "ng-zorro-antd/modal";
import { RestoreConfirmComponent } from "./restore-confirm/restore-confirm.component";
import {
  NzContextMenuService,
  NzDropdownMenuComponent,
  NzDropDownModule,
} from "ng-zorro-antd/dropdown";
import { NzAlertModule } from "ng-zorro-antd/alert";
import { NzTooltipDirective } from "ng-zorro-antd/tooltip";
import { NzBadgeComponent } from "ng-zorro-antd/badge";
import { NzTypographyComponent } from "ng-zorro-antd/typography";
import pLimit from "p-limit";

interface S3TaggingResponse {
  errors: Record<string, string>;
  success: string[];
}

/** p-limit rejects everything still queued with an AbortError when the user hits Cancel.
 *  That is a deliberate action, so it is reported as a notice and never as an error. */
function isAbortError(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === "AbortError";
}

/** A job folder is one holding a runfile; the real names are suffixed, e.g. `x-Runfile.key`. */
const RUNFILE_SUFFIX = "Runfile.key";

/** Past this many objects, folding the listing and rendering the tree is enough work to
 *  lock the page up for a noticeable stretch. The walk still runs — the user is just told
 *  first, so a frozen tab reads as "this is big" rather than "this broke". */
const LARGE_WALK_THRESHOLD = 50_000;

@Component({
  selector: "app-s3-explorer",
  imports: [
    NzDividerComponent,
    NzInputModule,
    NzButtonComponent,
    ReactiveFormsModule,
    NzFormModule,
    NzTreeModule,
    NzTagComponent,
    NzIconModule,
    NzSpaceModule,
    FormatBytesPipe,
    StorageClassConfirmComponent,
    DatePipe,
    NzSelectComponent,
    NzOptionComponent,
    NzModalModule,
    NzDropDownModule,
    NzAlertModule,
    NzSpaceCompactComponent,
    NzTooltipDirective,
    NzBadgeComponent,
    NzTypographyComponent,
  ],
  templateUrl: "./s3-explorer.component.html",
  styleUrl: "./s3-explorer.component.less",
})
export class S3ExplorerComponent {
  protected readonly limit = pLimit({
    concurrency: 8,
    rejectOnClear: true,
  });

  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly projectService = inject(ProjectService);
  private readonly cachedProjectService = inject(CachedProjectService);
  private readonly notification = inject(NzNotificationService);
  private readonly message = inject(NzMessageService);
  private readonly modalService = inject(NzModalService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly clipboard = inject(Clipboard);
  private readonly contextMenuService = inject(NzContextMenuService);

  private readonly tree = viewChild.required(NzTreeComponent);
  private readonly nodeMenu =
    viewChild.required<NzDropdownMenuComponent>("nodeMenu");

  /** Drives the confirmation modal: null closed, a class opens it for that action. */
  protected pendingTarget = signal<TargetStorageClass | null>(null);
  /** Snapshotted when an action starts, so it works from a selection that cannot shift
   *  under it while a modal is up. */
  protected checkedNodes = signal<NzTreeNode[]>([]);
  protected isApplying = signal(false);

  /** The folder the context menu was opened on, consumed by startWalk. */
  private contextNode: NzTreeNode | null = null;
  private readonly inFlight = new Map<string, Promise<NzTreeNode[]>>();
  protected isEvaluating = signal<boolean>(false);
  /** Cancels an in-flight recursive listing. p-limit's clearQueue only discards work that
   *  has not started, so a single long request needs the unsubscribe to abort it. */
  private readonly cancelWalk$ = new Subject<void>();

  protected treeNodes = signal<NzTreeNodeOptions[]>([]);
  protected prefixForm = new FormGroup({
    bucket: new FormControl("", [Validators.required]),
    prefix: new FormControl("", [Validators.pattern(/^(.+\/)?$/)]),
  });

  protected bucketsResource = rxResource<S3BucketInterface[], never>({
    stream: () =>
      this.cachedProjectService.getS3Buckets().pipe(
        catchError((e: HttpErrorResponse) => {
          const err = new Error(e.error?.detail ?? e.message);
          (err as any).status = e.status;
          (err as any).code = e.error?.code;
          return throwError(() => err);
        }),
      ),
  });

  ngOnInit() {
    const bucket = this.route.snapshot.queryParamMap.get("bucket") ?? "";
    const prefix = this.route.snapshot.queryParamMap.get("prefix") ?? "";

    if (bucket || prefix) {
      this.prefixForm.setValue({ bucket, prefix });
      // removes the bucket and prefix parameter in the URL.
      this.location.replaceState(this.location.path().split("?")[0]);

      this.onSubmitHandler();
    }
  }

  protected async onSubmitHandler() {
    if (this.prefixForm.valid) {
      this.prefixForm.disable();

      const values = this.prefixForm.getRawValue();
      // the await just makes the overall function async so i can wait for it when i want to
      await firstValueFrom(
        this.projectService.getS3KeyContents(values.bucket!, values.prefix!),
      )
        .then((data) => {
          const baseTreeNodes: NzTreeNodeOptions[] = [
            {
              // The bucket shows in the title only. The key is the prefix verbatim, so it
              // is bucket-relative like every other node and needs no special casing when
              // it is submitted. An empty prefix is the bucket root, and checking this node
              // then genuinely means "the whole bucket" — pass it through as such.
              title: [values.bucket, values.prefix].join("/"),
              key: values.prefix!,
              children: this.buildTreeData(data.results),
              isLeaf: false,
              expanded: true,
              // Its children come with it, so it is loaded by definition.
              loaded: true,
              // Load-bearing: the recursive walks skip anything not typed as a folder.
              type: "folder",
            },
          ];
          this.treeNodes.set(baseTreeNodes);
        })
        .catch((err: HttpErrorResponse) => {
          this.notification.error(
            "Error",
            err.error?.detail ?? "An unexpected error occurred",
          );
        })
        .finally(() => this.prefixForm.enable());
    }
  }

  private buildTreeData(data: S3Item[]) {
    return data.map((d) => {
      return {
        title: d.key.split("/").filter(Boolean).pop() || "",
        isLeaf: d.type == "file", // files are leaf, folders aren't
        loaded: d.type !== "folder", // this is the case for folders. if its a folder, this will be false, and once the folder has been FETCHED, this will be changed to true. it serves as a flag for if the folder has been fetched or not because children is an ambiguity, a folder that has not been fetched, or has 0 children, both appear the same in the nzTree internals
        ...d,
      };
    });
  }

  /**
   * Recursively calculates the size of the node itself IF everything within it has been calculated already
   * and traverses to parent, in the successful case
   *
   * @param {NzTreeNode} node - any node.
   */
  private calcSizeOfNode(node: NzTreeNode): void {
    const allCalculated = node.children.every(
      (child) => child.origin?.size != null,
    );
    if (allCalculated) {
      node.origin.size = node.children.reduce(
        (sum: number, item: NzTreeNode) => sum + (item.origin.size ?? 0),
        0,
      );
      if (node.parentNode) return this.calcSizeOfNode(node.parentNode);
    }
    return;
  }

  /**
   * Lists one level under a folder and attaches it. Shared by the expand handler
   * and the recursive walk, and deduplicated so the two can never collide.
   *
   * @param {NzTreeNode} node - the folder to list.
   * @returns the child nodes that are themselves folders.
   */
  private loadChildren(node: NzTreeNode): Promise<NzTreeNode[]> {
    const existing = this.inFlight.get(node.key);
    if (existing) return existing;

    const bucket = this.prefixForm.getRawValue().bucket!;
    node.isLoading = true;
    node.update();

    const request = this.limit(() =>
      firstValueFrom(this.projectService.getS3KeyContents(bucket, node.key)),
    )
      .then((data) => {
        const results: S3Item[] = data.results ?? [];
        // A folder holding only files — or nothing at all, since [].every() is
        // true — can be sized right away. A mixed one waits for its subfolders.
        if (results.every((res) => res.type === "file")) {
          node.origin.size = results.reduce(
            (sum: number, item: S3Item) => sum + (item.size ?? 0),
            0,
          );
          if (node.parentNode) this.calcSizeOfNode(node.parentNode);
        }

        this.mergeChildren(node, this.buildTreeData(results));
        node.origin.loaded = true;
        node.update();
        return node.getChildren().filter((child) => !child.isLeaf);
      })
      .finally(() => {
        this.inFlight.delete(node.key);
        // addChildren clears this on success but not when the request failed,
        // which would otherwise leave the row spinning forever.
        node.isLoading = false;
        node.update();
      });

    this.inFlight.set(node.key, request);
    return request;
  }

  /** The immediate children of a folder, listing it first unless it is already loaded. */
  private async childrenOf(node: NzTreeNode): Promise<NzTreeNode[]> {
    if (!node.origin.loaded) await this.loadChildren(node);
    return node.getChildren();
  }

  /**
   * Folds freshly built options into whatever the node already holds.
   *
   * Merging rather than replacing keeps check state, dashboard tags and already-loaded
   * contents alive, and addChildren appends blindly so a plain call would duplicate every
   * key the node already had. It has to recurse: a folder the user happened to browse
   * earlier already exists, and skipping it wholesale would discard the entire subtree
   * the fold built beneath it.
   *
   * Only one addChildren fires per node that actually gains children — normally just the
   * one at the top, which is the point, since each call re-flattens the whole tree.
   */
  private mergeChildren(node: NzTreeNode, children: NzTreeNodeOptions[]): void {
    const existing = new Map(
      node.getChildren().map((child) => [child.key, child] as const),
    );
    const additions: NzTreeNodeOptions[] = [];

    for (const child of children) {
      const match = existing.get(child.key);
      if (!match) {
        additions.push(child);
        continue;
      }
      // Keep the live node and fold this branch in underneath it.
      if (child.children?.length) this.mergeChildren(match, child.children);
      // Both only ever upgrade: a skeleton merge must not mark a fully listed folder
      // unlisted, nor erase a size it already knows.
      if (child["loaded"]) match.origin.loaded = true;
      if (child["size"] != null) match.origin.size = child["size"];
      if (child.expanded) match.setExpanded(true);
    }

    if (additions.length) node.addChildren(additions);
  }

  /**
   * Lists every object beneath a prefix in one request.
   *
   * @returns the flat listing, or null if the user cancelled it.
   */
  private async listRecursively(prefix: string): Promise<S3Item[] | null> {
    const bucket = this.prefixForm.getRawValue().bucket!;
    const data = await firstValueFrom(
      this.projectService
        .getS3KeyContents(bucket, prefix, true)
        .pipe(takeUntil(this.cancelWalk$)),
      // Cancelling unsubscribes, which completes the stream without a value; a default
      // turns that into null instead of an EmptyError to unpick further up.
      { defaultValue: null },
    );
    return data?.results ?? null;
  }

  /** One traversal, reused for every lookup. getTreeNodeByKey re-flattens the whole tree
   *  on each call, so calling it in a loop is quadratic in the size of the tree. */
  private indexTree(): Map<string, NzTreeNode> {
    const index = new Map<string, NzTreeNode>();
    const visit = (node: NzTreeNode) => {
      index.set(node.key, node);
      node.getChildren().forEach(visit);
    };
    this.tree().getTreeNodes().forEach(visit);
    return index;
  }

  protected nzEvent(event: NzFormatEmitEvent): void {
    if (event.eventName !== "expand") return;

    const node = event.node;
    if (!node) return;

    if (node.origin.loaded) {
      // An already-loaded folder that is genuinely empty has nothing to add, so clear it here. Otherwise the row spins forever, and since clickExpand ignores aloading node, it can never be collapsed again either.
      if (node.isLoading) {
        node.isLoading = false;
        node.update();
      }
      return;
    }

    if (!node.isExpanded) return;

    this.loadChildren(node).catch((err: unknown) => {
      // A node queued behind Cancel rejects too; that is not worth an error toast.
      if (isAbortError(err)) return;
      this.notification.error(
        "Error",
        httpErrorMessage(err as HttpErrorResponse),
      );
    });
  }

  /** Right-click on a folder row opens the menu at the cursor. */
  protected onContextMenu(event: NzFormatEmitEvent): void {
    const node = event.node;
    if (!node || node.isLeaf) return; // nothing to walk on a file
    this.contextNode = node;
    this.contextMenuService.create(event.event as MouseEvent, this.nodeMenu());
  }

  protected navigateToHere() {
    if (this.contextNode) {
      this.prefixForm.get("prefix")!.setValue(this.contextNode.origin.key);
      this.onSubmitHandler();
    }
  }

  /** ng-zorro status presets, not palette presets or hex: they render the soft tinted
   *  tag and follow the theme, where a hex value forces the loud filled variant. */
  classColor: Record<string, string> = {
    STANDARD: "success",
    GLACIER_IR: "processing",
    DEEP_ARCHIVE: "default",
  };
  classLabel: Record<string, string> = {
    STANDARD: "Standard",
    GLACIER_IR: "Glacier IR",
    DEEP_ARCHIVE: "Deep Archive",
  };

  /** Opens the confirmation. The modal does the file/folder split and drops anything
   *  that is not Standard. */
  protected markSelectedFor(target: TargetStorageClass) {
    this.checkedNodes.set(this.tree().getCheckedNodeList());
    this.pendingTarget.set(target);
  }

  /**
   * Lists the whole subtree under one folder in one request and attaches it in one go.
   * Both matter: a per-folder walk is serialised by depth, and every addChildren
   * re-flattens the entire tree, so attaching F folders separately costs O(N²) on the
   * main thread.
   *
   * @param open - also expand everything, rather than only measuring it.
   * @returns false if the user cancelled the listing.
   */
  private async walkNode(node: NzTreeNode, open: boolean): Promise<boolean> {
    const items = await this.listRecursively(node.key);
    if (!items) return false;

    if (items.length > LARGE_WALK_THRESHOLD) {
      this.notification.warning(
        "Large prefix",
        `${items.length.toLocaleString()} objects under this prefix. Building the tree will take a moment, and the page will not respond while it does.`,
        { nzDuration: 8000 },
      );
      // Everything below is synchronous, so without yielding the browser a turn the
      // warning would only paint once the work it warns about had already finished.
      await new Promise((resolve) => setTimeout(resolve));
    }

    this.mergeChildren(node, foldS3Keys(node.key, items, { expanded: open }));
    // The listing was exhaustive, so this is the exact size and the folder never needs
    // listing again.
    node.origin.size = items.reduce((sum, item) => sum + (item.size ?? 0), 0);
    node.origin.loaded = true;
    if (open) this.expand(node);
    node.update();
    if (node.parentNode) this.calcSizeOfNode(node.parentNode);
    return true;
  }

  /**
   * Expands a folder from code.
   *
   */
  private expand(node: NzTreeNode): void {
    node.setExpanded(true);
    node.update();
    this.tree().renderTree();
  }

  /** Walks the folder the context menu was opened on. */
  protected async startWalk(open: boolean = false) {
    const node = this.contextNode;
    if (!node) {
      this.message.info("No node selected");
      return;
    }

    this.isEvaluating.set(true);
    try {
      if (!(await this.walkNode(node, open))) {
        this.message.info("Aborted recursive walk");
      }
    } catch (err: unknown) {
      this.notification.error(
        "Error",
        httpErrorMessage(err as HttpErrorResponse),
      );
    } finally {
      this.isEvaluating.set(false);
    }
  }

  /** Sizes every checked folder exactly. Files already carry the size the listing that
   *  produced them gave them, so they are dropped. */
  protected async calculateSelectedSizes() {
    const nodes = this.tree()
      .getCheckedNodeList()
      .filter((node) => node.origin.type === "folder");

    if (nodes.length === 0) {
      this.message.warning("Please select folders to calculate");
      return;
    }

    this.isEvaluating.set(true);
    try {
      // Sequential: each of these is one exhaustive listing, so a selection of twenty
      // folders would otherwise fire twenty of them at once.
      for (const node of nodes) {
        if (!(await this.walkNode(node, false))) {
          this.message.info("Size calculation cancelled");
          return; // finally still runs, resets the signal
        }
      }
    } catch (err: unknown) {
      this.notification.error(
        "Error",
        httpErrorMessage(err as HttpErrorResponse),
      );
    } finally {
      this.isEvaluating.set(false);
    }
  }

  /** Clears the tick on every checked row. */
  protected uncheckAll() {
    // Copied: unchecking a node removes it from the very list being iterated.
    const nodes = [...this.tree().getCheckedNodeList()];
    for (const node of nodes) {
      node.setChecked(false);
      node.update();
    }
    this.checkedNodes.set([]);
    this.message.info(`Unchecked ${nodes.length} item(s)`);
  }

  /** Ticks every row the dashboard check came back negative on, anywhere in the tree.
   *  A row carrying no verdict was never checked, or is not a job, so it is left alone.
   *  Additive, like every other select here — Uncheck all is how a selection is cleared. */
  protected selectNotOnDashboard() {
    const nodes = [...this.indexTree().values()].filter(
      (node) => node.origin.isOnDashboard === false,
    );

    if (nodes.length === 0) {
      this.message.info("Nothing is marked as not on dashboard");
      return;
    }

    for (const node of nodes) {
      node.setChecked(true);
      node.update()
    }
    this.checkedNodes.set(this.tree().getCheckedNodeList());
    this.message.info(`Selected ${nodes.length} item(s)`);
  }

  /** Ticks every immediate child of the right-clicked folder, listing it first if it has
   *  never been opened. nzCheckStrictly means a tick on the parent never reaches them. */
  protected async selectChildren() {
    const node = this.contextNode;
    if (!node) {
      this.message.info("No node selected");
      return;
    }

    this.isEvaluating.set(true);
    try {
      const children = await this.childrenOf(node);
      for (const child of children) {
        child.setChecked(true);
      }
      // Selecting rows the user cannot see reads as nothing having happened.
      this.expand(node);
      this.message.info(`Selected ${children.length} item(s)`);
    } catch (err: unknown) {
      // A listing queued behind Cancel rejects too; that is a deliberate action.
      if (isAbortError(err)) return;
      this.notification.error(
        "Error",
        httpErrorMessage(err as HttpErrorResponse),
      );
    } finally {
      this.isEvaluating.set(false);
    }
  }

  /** Stops the queued single-level expands and aborts any recursive listing in flight. */
  protected cancelPromises() {
    this.limit.clearQueue();
    this.cancelWalk$.next();
  }

  protected async checkIfOnDashboard(): Promise<void> {
    const bucket = this.prefixForm.get("bucket")!.value;
    const nodes = this.tree()
      .getCheckedNodeList()
      .filter((node) => node.origin.type === "folder");
    this.checkedNodes.set(nodes);

    if (nodes.length === 0) {
      this.message.warning("Please select folders to check");
      return;
    }

    this.isEvaluating.set(true);
    try {
      // One single-level listing per selected folder, and no descent from there: a folder
      // holding a runfile is itself the job, and any other one is a container whose
      // immediate subfolders are the candidates. Whether those are jobs is the backend's
      // question to answer, so they go over as they are.
      const keys = new Set<string>();
      await Promise.all(
        nodes.map(async (node) => {
          const children = await this.childrenOf(node);
          if (
            children.some((c) => c.isLeaf && c.key.endsWith(RUNFILE_SUFFIX))
          ) {
            keys.add(node.key);
          } else {
            for (const c of children) if (!c.isLeaf) keys.add(c.key);
          }
        }),
      );

      const index = this.indexTree();
      const keysToCheck = [...keys].filter(
        // Anything already carrying a verdict was checked on an earlier run.
        (key) => index.get(key)?.origin.isOnDashboard === undefined,
      );

      if (keysToCheck.length === 0) {
        this.message.info(
          "Nothing new to check — the selected folders are empty or already evaluated.",
        );
        return;
      }

      // await this so isEvaluating stays true until the check completes
      const res = await firstValueFrom(
        this.projectService.checkPathExists(
          keysToCheck.map((k) => [bucket, k].join("/")),
        ),
      );
      const paths: Record<string, any> = res["results"];
      for (const [key, value] of Object.entries(paths)) {
        // Not a job at all: null, or left out of the response entirely. It gets no
        // verdict, so the row renders no tag rather than a misleading red one.
        if (value == null) continue;
        // the key from the backend includes the bucket, so remove the first section
        const currentNode = index.get(key.slice(key.indexOf("/") + 1));
        if (currentNode) {
          // The job's DTO when it is on the dashboard, false when it is not.
          currentNode.origin.isOnDashboard = !!value;
          currentNode.update();
        }
      }
    } catch (err) {
      // A listing queued behind Cancel rejects too; that is a deliberate action.
      if (isAbortError(err)) {
        this.message.info("Evaluation cancelled");
        return;
      }
      this.notification.error(
        "Error",
        httpErrorMessage(err as HttpErrorResponse),
      );
    } finally {
      this.isEvaluating.set(false);
    }
  }

  protected async applyStorageClass(request: StorageClassRequest) {
    if (this.isApplying()) return;

    const body = {
      bucket: this.prefixForm.controls.bucket.value ?? "",
      storage_class: request.target,
      files: request.files.map((node) => node.origin.key),
      folders: request.folders.map((node) => node.origin.key),
    };
    const label = this.classLabel[request.target];

    this.isApplying.set(true);
    // Sticky (nzDuration 0) and removed by id — remove() with no argument would clear
    // every unrelated toast on screen too.
    const loading = this.message.loading(
      `Moving ${body.files.length + body.folders.length} item(s) to ${label}…`,
      { nzDuration: 0 },
    );

    try {
      const response = await firstValueFrom<{
        files: S3TaggingResponse;
        folders: S3TaggingResponse;
      }>(this.projectService.applyS3StorageClass(body));
      for (const [key, value] of [
        ...Object.entries(response.files.errors),
        ...Object.entries(response.folders.errors),
      ]) {
        this.notification.warning(key, value, { nzDuration: 5000 });
      }
      this.notification.success("Success", `Moved to ${label}.`);
    } catch (err) {
      this.notification.error(
        "Error",
        httpErrorMessage(err as HttpErrorResponse),
      );
    } finally {
      this.message.remove(loading.messageId);
      this.isApplying.set(false);
    }
  }

  protected async markForRestoration(files: string[], folders: string[]) {
    const bucket = this.prefixForm.get("bucket")!.value!;
    // we have pretty much ensured by this point that the bucked does exist

    this.isApplying.set(true);
    await firstValueFrom(
      this.projectService.restoreS3Objects(bucket, files, folders),
    )
      .then((res) => {
        console.log("res from restore", res);
        const {
          errors,
          skipped,
          success,
        }: {
          errors: Record<string, any>;
          skipped: string[];
          success: string[];
        } = res["results"];

        if (success.length > 0)
          this.notification.success(
            "",
            `Restoration commenced for ${success.length} files`,
          );
        if (skipped.length > 0)
          this.notification.info(
            "",
            `Skipped ${skipped.length} files as they were not archived`,
          );
        Object.entries(errors).forEach(([key, value]) =>
          this.notification.error(key, value),
        );
      })
      .catch((err: HttpErrorResponse) => {
        console.log("error", err);
        this.notification.error(
          "Error",
          err.error?.detail ?? "An unexpected error occurred",
        );
      })
      .finally(() => this.isApplying.set(false));
  }
  protected openRestorationModal() {
    // get selected to be marked for restoration ONLY if they are in Glacier
    this.checkedNodes.set(this.tree().getCheckedNodeList());
    if (this.checkedNodes().length == 0) {
      this.message.warning("Please select files to restore");
      return;
    }

    const bucket = this.prefixForm.get("bucket")?.value;
    if (!bucket) {
      this.message.warning("No bucket selected.");
      return;
    }

    this.modalService.create<
      RestoreConfirmComponent,
      { bucket: string | undefined; nodes: any[] }
    >({
      nzTitle: "Restore objects from Deep Archive",
      nzContent: RestoreConfirmComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzWidth: 680,
      nzBodyStyle: {
        "max-height": "calc(100vh - 220px)",
        "overflow-y": "auto",
      },
      nzData: {
        bucket: bucket,
        nodes: this.checkedNodes().map((node) => node.origin),
      },
      nzOnOk: (stuff) => {
        const files = stuff.eligibleFiles().map((node) => node.key);
        const folders = stuff.folders().map((node) => node.key);
        this.markForRestoration(files, folders);
      },
    });
  }

  /**
   * Copies the checked rows to the clipboard.
   *
   * @param withInfo - the whole node data as JSON rather than one bare key per line.
   *   `children` is dropped at every depth: it is tree plumbing, not information about
   *   the row, and a folder that has been walked carries its entire subtree in it.
   */
  protected copySelection(withInfo: boolean = false): void {
    const nodes = this.tree().getCheckedNodeList();
    if (nodes.length === 0) {
      this.message.warning("Please select something to copy");
      return;
    }

    this.clipboard.copy(
      withInfo
        ? JSON.stringify(
            nodes.map((node) => ({
              title: node.origin.title,
              type: node.origin.type,
              key: node.origin.key,
              size: node.origin.size,
            })),
            (key, value) => (key === "children" ? undefined : value),
            2,
          )
        : nodes.map((node) => node.origin.key).join("\n"),
    );
    this.message.info(`Copied ${nodes.length} key(s)`);
  }

  /**
   * Copies link to the current bucket and key in the explorer so that it can be shared
   */
  protected copyLink() {
    const url = new URL(window.location.href);
    const vals = this.prefixForm.getRawValue();
    url.searchParams.set("bucket", vals.bucket ?? "");
    url.searchParams.set("prefix", vals.prefix ?? "");
    this.clipboard.copy(url.toString());
    this.message.info("Copied link!");
  }
}
